import { serverTimestamp } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import { HiOutlineXMark, HiOutlineUserGroup } from "react-icons/hi2";
import { useAuth } from "../../../context/AuthContext";

const inputBase =
  "w-full px-4 py-3 rounded-xl border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-colors";

const glassBtn =
  "inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#4c2bd4] dark:text-[#c4b5fd] backdrop-blur-sm hover:bg-[#5932EA]/18 dark:hover:bg-[#5932EA]/25 transition-colors disabled:opacity-60";

const glassBtnMuted =
  "inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/60 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700 transition-colors";

interface GroupModalProps {
  group: any | null;
  onSave: (group: any) => void | Promise<void>;
  onClose: () => void;
  saving?: boolean;
}

const GroupModal: React.FC<GroupModalProps> = ({
  group,
  onSave,
  onClose,
  saving = false,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (group) {
      setName(group.name || "");
      setDescription(group.description || "");
    } else {
      setName("");
      setDescription("");
    }
  }, [group]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave({
      name: name.trim(),
      description: description.trim(),
      timestamp: serverTimestamp(),
      userId: user?.uid,
    });
  };

  return (
    <AnimatedModal isOpen onClose={onClose} contentClassName="sm:max-w-md">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100/80 dark:border-gray-700/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA]">
              <HiOutlineUserGroup className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
                {group ? t("editGroup") : t("addGroup")}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {group ? t("editGroupSubtitle") : t("addGroupSubtitle")}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="p-5 sm:p-6 space-y-4 overflow-y-auto flex-1">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2"
              >
                {t("groupName")}
              </label>
              <input
                maxLength={150}
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className={inputBase}
                placeholder={t("groupName")}
              />
            </div>
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2"
              >
                {t("description")}
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                maxLength={500}
                className={`${inputBase} min-h-[100px] resize-y`}
                placeholder={t("description")}
              />
            </div>
          </div>
          <div className="px-4 sm:px-6 py-4 border-t border-gray-100/80 dark:border-gray-700/80 bg-gray-50/40 dark:bg-gray-900/20 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 shrink-0">
            <button type="button" onClick={onClose} className={glassBtnMuted} disabled={saving}>
              {t("cancel")}
            </button>
            <button type="submit" className={`${glassBtn} sm:ml-auto`} disabled={saving}>
              {saving ? (
                <>
                  <span className="w-4 h-4 border-2 border-[#5932EA]/30 border-t-[#5932EA] rounded-full animate-spin" />
                  {t("saving")}
                </>
              ) : (
                t("save")
              )}
            </button>
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
};

export default GroupModal;
